// Offer & Proposal — ONE shared commercial model: quotation, proposal preview and
// pipeline value always update together (totals computed server-side).
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Copy, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { API } from "./SalesCRM";

const EMPTY = { items: [], travel_fee: 0, props_fee: 0, talent_fee: 0, revision_fee: 0, custom_fees: [],
    discount: 0, deposit_pct: 50, validity_date: "", shoot_date: "", delivery_timeline: "", terms: "", notes: "" };

export default function OfferSection({ id, reload }) {
    const [offer, setOffer] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [texts, setTexts] = useState({});
    const [shareToken, setShareToken] = useState(null);
    const [busy, setBusy] = useState(false);
    const [genBusy, setGenBusy] = useState("");

    const load = useCallback(async () => {
        const { data } = await axios.get(`${API}/crm/leads/${id}/offer`);
        setOffer(data.offer || { ...EMPTY });
        setCatalog(data.catalog || []);
        setTexts(data.proposal_texts || {});
        setShareToken(data.share_token);
    }, [id]);
    useEffect(() => { load(); }, [load]);

    if (!offer) return null;
    const set = (k) => (e) => setOffer({ ...offer, [k]: e.target.value });
    const setNum = (k) => (e) => setOffer({ ...offer, [k]: parseFloat(e.target.value) || 0 });

    const addPackage = (pkgId) => {
        const p = catalog.find((x) => x.id === pkgId);
        if (!p) return;
        const price = parseFloat(String(p.price || "0").replace(/[^\d.]/g, "")) || 0;
        setOffer({ ...offer, items: [...offer.items, { package_id: p.id, name: p.name_en, qty: 1, unit_price: price, deliverables: "", price_override_reason: "" }] });
    };
    const addCustom = () => setOffer({ ...offer, items: [...offer.items, { package_id: "", name: "Custom line item", qty: 1, unit_price: 0, deliverables: "", price_override_reason: "" }] });
    const setItem = (i, k, v) => {
        const items = offer.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it);
        setOffer({ ...offer, items });
    };
    const moveItem = (i, dir) => {
        const items = [...offer.items]; const j = i + dir;
        if (j < 0 || j >= items.length) return;
        [items[i], items[j]] = [items[j], items[i]];
        setOffer({ ...offer, items });
    };
    const removeItem = (i) => setOffer({ ...offer, items: offer.items.filter((_, idx) => idx !== i) });

    // live client-side preview of totals (authoritative totals come from server on save)
    const sub = offer.items.reduce((n, it) => n + (parseFloat(it.unit_price) || 0) * (parseInt(it.qty) || 1), 0);
    const fees = (parseFloat(offer.travel_fee) || 0) + (parseFloat(offer.props_fee) || 0) + (parseFloat(offer.talent_fee) || 0)
        + (parseFloat(offer.revision_fee) || 0) + (offer.custom_fees || []).reduce((n, f) => n + (parseFloat(f.amount) || 0), 0);
    const total = Math.max(0, sub + fees - (parseFloat(offer.discount) || 0));
    const dep = total * (parseFloat(offer.deposit_pct) || 0) / 100;

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await axios.put(`${API}/crm/leads/${id}/offer`, offer);
            setOffer(data.offer);
            toast.success(`Offer saved — pipeline value updated to RM ${Math.round(data.offer.total).toLocaleString()}`);
            reload();
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };
    const gen = async (lang) => {
        setGenBusy(lang);
        try {
            const { data } = await axios.post(`${API}/crm/leads/${id}/offer/generate`, { lang });
            setTexts({ ...texts, [lang]: data.text });
            toast.success("Proposal text ready");
        } catch (err) { toast.error(err?.response?.data?.detail || "Generation failed"); }
        finally { setGenBusy(""); }
    };

    const Num = ({ label, k }) => (
        <label className="block text-xs text-neutral-600">{label}
            <input data-testid={`offer-${k}`} type="number" value={offer[k]} onChange={setNum(k)}
                className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
        </label>
    );

    return (
        <div className="space-y-3" data-testid="section-offer">
            <div className="bg-white rounded-xl border border-neutral-200 p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-2">
                    Quotation & proposal — one shared model (prices load from Offers & Pricing)
                </div>
                <div className="flex flex-wrap gap-2">
                    <select data-testid="offer-add-package" value="" onChange={(e) => e.target.value && addPackage(e.target.value)}
                        className="border rounded-full px-2.5 py-1.5 text-xs bg-white">
                        <option value="">+ Add from catalogue…</option>
                        {catalog.map((p) => <option key={p.id} value={p.id}>{p.name_en}{p.price ? ` — RM${p.price}` : ""}</option>)}
                    </select>
                    <button data-testid="offer-add-custom" onClick={addCustom} className="text-xs px-3 py-1.5 rounded-full border">+ Custom line item</button>
                </div>
                {offer.items.map((it, i) => (
                    <div key={i} className="border border-neutral-100 rounded-lg p-2 mt-2" data-testid={`offer-item-${i}`}>
                        <div className="flex gap-2 items-center">
                            <input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} className="flex-1 border rounded px-2 py-1 text-sm" data-testid={`offer-item-name-${i}`} />
                            <input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", parseInt(e.target.value) || 1)} className="w-14 border rounded px-2 py-1 text-sm" title="Qty" data-testid={`offer-item-qty-${i}`} />
                            <input type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", parseFloat(e.target.value) || 0)} className="w-24 border rounded px-2 py-1 text-sm" title="Unit price RM" data-testid={`offer-item-price-${i}`} />
                            <button onClick={() => moveItem(i, -1)} className="p-1 text-neutral-400"><ArrowUp size={13} /></button>
                            <button onClick={() => moveItem(i, 1)} className="p-1 text-neutral-400"><ArrowDown size={13} /></button>
                            <button data-testid={`offer-item-remove-${i}`} onClick={() => removeItem(i)} className="p-1 text-red-500"><Trash2 size={13} /></button>
                        </div>
                        <input value={it.deliverables} onChange={(e) => setItem(i, "deliverables", e.target.value)} placeholder="Deliverables"
                            className="mt-1 w-full border rounded px-2 py-1 text-xs" data-testid={`offer-item-deliv-${i}`} />
                        {it.package_id && <input value={it.price_override_reason} onChange={(e) => setItem(i, "price_override_reason", e.target.value)}
                            placeholder="If you changed the catalogue price, record why…" className="mt-1 w-full border border-amber-200 rounded px-2 py-1 text-[11px]" data-testid={`offer-item-override-${i}`} />}
                    </div>
                ))}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                    <Num label="Travel fee" k="travel_fee" /><Num label="Props fee" k="props_fee" />
                    <Num label="Talent fee" k="talent_fee" /><Num label="Extra revisions" k="revision_fee" />
                    <Num label="Discount (RM)" k="discount" /><Num label="Deposit %" k="deposit_pct" />
                    <label className="block text-xs text-neutral-600">Validity date
                        <input data-testid="offer-validity" type="date" value={offer.validity_date} onChange={set("validity_date")} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" /></label>
                    <label className="block text-xs text-neutral-600">Est. shoot date
                        <input data-testid="offer-shoot-date" type="date" value={offer.shoot_date} onChange={set("shoot_date")} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" /></label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    <label className="block text-xs text-neutral-600">Delivery timeline
                        <input data-testid="offer-timeline" value={offer.delivery_timeline} onChange={set("delivery_timeline")} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" /></label>
                    <label className="block text-xs text-neutral-600">Terms
                        <input data-testid="offer-terms" value={offer.terms} onChange={set("terms")} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" /></label>
                </div>
                <div className="flex flex-wrap items-center justify-between mt-4 gap-2 bg-[#F5F0E6] rounded-lg px-3 py-2">
                    <div className="text-sm" data-testid="offer-totals">
                        Subtotal RM {sub.toLocaleString()} · Fees RM {fees.toLocaleString()} · <b className="text-cafe-espresso">Total RM {total.toLocaleString()}</b>
                        <span className="text-xs text-neutral-500"> · Deposit RM {Math.round(dep).toLocaleString()} / Balance RM {Math.round(total - dep).toLocaleString()}</span>
                    </div>
                    <button data-testid="offer-save-btn" disabled={busy} onClick={save}
                        className="px-4 py-2 text-xs rounded-full bg-cafe-espresso text-white">{busy ? "Saving…" : "Save draft (syncs quotation + proposal + pipeline)"}</button>
                </div>
            </div>
            <div className="bg-white rounded-xl border border-neutral-200 p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-2">Proposal text</div>
                <div className="flex flex-wrap gap-2">
                    {[["en", "Generate English"], ["cn", "Generate 中文"], ["both", "Generate bilingual"]].map(([l, label]) => (
                        <button key={l} data-testid={`proposal-gen-${l}`} disabled={!!genBusy || !offer.updated_at} onClick={() => gen(l)}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border disabled:opacity-40">
                            <Sparkles size={12} /> {genBusy === l ? "Writing…" : label}
                        </button>
                    ))}
                    {shareToken && (
                        <button data-testid="proposal-copy-link" onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/proposal/${shareToken}`);
                            toast.success("Public proposal link copied");
                        }} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border"><Copy size={12} /> Copy public link</button>
                    )}
                </div>
                {!offer.updated_at && <div className="text-xs text-neutral-400 mt-2">Save the offer first, then generate the proposal text.</div>}
                {Object.entries(texts).map(([l, t]) => t && (
                    <div key={l} className="mt-3 border-t pt-2" data-testid={`proposal-text-${l}`}>
                        <div className="flex justify-between items-center">
                            <span className="text-[10px] uppercase text-neutral-500">{l === "en" ? "English" : l === "cn" ? "中文" : "Bilingual"}</span>
                            <button onClick={() => { navigator.clipboard.writeText(t); toast.success("Copied"); }} className="text-xs underline">Copy</button>
                        </div>
                        <p className="text-sm whitespace-pre-wrap mt-1 max-h-64 overflow-y-auto">{t}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function ConvertSection({ id, reload }) {
    const [prev, setPrev] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { axios.get(`${API}/crm/leads/${id}/convert-preview`).then((r) => setPrev(r.data)).catch(() => {}); }, [id]);
    if (!prev) return null;
    if (prev.already_converted) {
        return (
            <div className="bg-lime-50 border border-cafe-olive/30 rounded-xl p-4 text-sm" data-testid="convert-done">
                ✓ Already converted to Client Project.
                <a href={`/studio?tab=clients`} className="underline ml-1 text-cafe-olive" data-testid="convert-open-project">Open Client Projects</a>
            </div>
        );
    }
    const convert = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/crm/leads/${id}/convert`);
            toast.success("Client Project created");
            setPrev({ ...prev, already_converted: true, converted_project_id: data.project_id });
            reload();
        } catch (err) {
            const det = err?.response?.data?.detail;
            toast.error(typeof det === "string" ? det : det?.message || "Conversion failed");
        } finally { setBusy(false); }
    };
    return (
        <div className="bg-white border-2 border-cafe-terracotta/50 rounded-xl p-4" data-testid="convert-section">
            <div className="text-[10px] uppercase tracking-[0.15em] text-cafe-terracotta mb-2">Convert to Client Project — review before confirming</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div><b>Brand:</b> {prev.brand}</div>
                <div><b>Contact:</b> {[prev.contact.name, prev.contact.email, prev.contact.phone].filter(Boolean).join(" · ") || "—"}</div>
                <div><b>Location:</b> {prev.location || "—"}</div>
                <div><b>Project value:</b> RM {Math.round(prev.total).toLocaleString()}</div>
                <div><b>Deposit:</b> {prev.deposit_pct ? `${prev.deposit_pct}% = RM ${Math.round(prev.deposit_amount || 0).toLocaleString()}` : "50% default"}</div>
                <div><b>Balance:</b> RM {Math.round(prev.balance || prev.total / 2).toLocaleString()}</div>
                <div><b>Shoot date:</b> {prev.shoot_date || "TBC"}</div>
                <div><b>Delivery:</b> {prev.delivery_timeline || "TBC"}</div>
            </div>
            {prev.offer_items.length > 0 && (
                <div className="text-xs mt-2"><b>Accepted offer:</b> {prev.offer_items.map((i) => `${i.name} x${i.qty}`).join(", ")}</div>
            )}
            {prev.deliverables.length > 0 && <div className="text-xs mt-1"><b>Deliverables:</b> {prev.deliverables.join("; ")}</div>}
            {prev.notes && <div className="text-xs mt-1"><b>Notes:</b> {prev.notes.slice(0, 200)}</div>}
            <div className="text-[11px] text-neutral-500 mt-2">The lead is kept with its full activity history and marked Converted. Duplicate projects are blocked.</div>
            <button data-testid="convert-confirm-btn" disabled={busy} onClick={convert}
                className="mt-3 px-5 py-2 text-sm rounded-full bg-cafe-terracotta text-white">
                {busy ? "Converting…" : "Confirm — create Client Project"}
            </button>
        </div>
    );
}
